import type { LanguageBundle } from '../config';
import type { LanguageCode } from '../languages';
import en from './en.json';

// Library-owned translations for the shared components (the `shared` namespace). English is
// bundled eagerly (default + fallback); the other languages are code-split and loaded on
// demand, mirroring how each app ships its own bundles. createI18n merges these into every
// app instance, and useSharedTranslation uses the English bundle as a standalone fallback
// for host apps that have no i18n at all.
export const sharedDefaultBundle: LanguageBundle = en;

// Partial on purpose: the shared package may not have a translation for every language an
// app ships. A missing entry means shared component strings fall back to English for that
// language (handled below), while the app's own strings still translate.
const loaders: Partial<Record<Exclude<LanguageCode, 'en'>, () => Promise<{ default: LanguageBundle }>>> = {
  es: () => import('./es.json'),
  fr: () => import('./fr.json'),
  de: () => import('./de.json'),
  ja: () => import('./ja.json'),
  pt: () => import('./pt.json'),
  'pt-BR': () => import('./pt-BR.json'),
  ar: () => import('./ar.json'),
  cs: () => import('./cs.json'),
  da: () => import('./da.json'),
  it: () => import('./it.json'),
  ko: () => import('./ko.json'),
  nl: () => import('./nl.json'),
  pl: () => import('./pl.json'),
  sv: () => import('./sv.json'),
};

export async function loadSharedBundle(code: LanguageCode): Promise<LanguageBundle> {
  if (code === 'en') return en;
  const loader = loaders[code];
  // No shared translation for this language: fall back to the English shared strings.
  if (!loader) return en;
  return (await loader()).default;
}
